// What needs you, by place (Craig, 30 Oct 2026; docs/specs/CAMPAIGN_LED_2026_10.md "Attention in the menu"): Home went,
// so every task waiting for a person counts on the page it is resolved on (its first home in lib/nav.ts TASK_HOMES),
// and your own to-dos due now on Conversations › Next steps. The menu shows the counts beside each place and page;
// Ask Ogma opens with them as cards.
import { createContext } from "react";
import { locate, TASK_HOMES } from "./nav";
import { isDue, isNextStep } from "./inbox";
import type { HumanTask } from "./types";
import type { OwnerTask } from "./ownerTasks";

/** Where your own to-dos are done. */
export const TODOS_PATH = "/conversations/next-steps";

export interface Attention {
  /** By page path. */
  pages: Record<string, number>;
  /** By menu place (section key). */
  places: Record<string, number>;
  total: number;
}

export function attention(tasks: Pick<HumanTask, "kind" | "entity_type" | "payload">[], todos: Pick<OwnerTask, "bucket">[]): Attention {
  const pages: Record<string, number> = {};
  const add = (path: string, n = 1) => {
    pages[path] = (pages[path] ?? 0) + n;
  };
  // A person's next step comes with the to-dos (its day decides whether it is due), so it is not counted twice.
  for (const t of tasks) {
    if (isNextStep(t)) continue;
    const home = TASK_HOMES[t.kind]?.[0];
    if (home) add(home);
  }
  const due = todos.filter(isDue).length;
  if (due) add(TODOS_PATH, due);
  const places: Record<string, number> = {};
  let total = 0;
  for (const [path, n] of Object.entries(pages)) {
    const sec = locate(path)?.section.key;
    if (!sec) continue;
    places[sec] = (places[sec] ?? 0) + n;
    total += n;
  }
  return { pages, places, total };
}

/** A tab's count: its pages' counts added up (the tab's own path and every page beneath it). */
export const tabCount = (a: Attention, tabPath: string): number =>
  Object.entries(a.pages).filter(([p]) => p === tabPath || p.startsWith(`${tabPath}/`)).reduce((n, [, c]) => n + c, 0);

/** The counts the menu shows, shared with the page (Layout reads them once; Ask Ogma's cards use the same). */
export const AttentionContext = createContext<Attention | null>(null);
